import React from "react";
import type { CallLogDto, CallDirection } from "../../services/telephonyService";

/**
 * Geteilte Helfer & Inline-Icons der Telefon-Seite (SP3/SP4).
 *
 * Bündelt die Formatierungs-/Ableitungs-Logik und die kvpIcons-Stil-Icons, die
 * sowohl die Liste (`TelephonyPage`) als auch das Detail (`CallDetailPanel`)
 * brauchen — eine Quelle, kein Duplikat, kein Zirkel-Import zwischen den beiden.
 */

// ── kleine Inline-Icons im kvpIcons-Stil (Strich 1.7, rund) ─────────────────
const ico = (d: React.ReactNode, size = 18): React.ReactElement => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {d}
    </svg>
);

export const ArrowInIcon = ico(
    <>
        <path d="M13 6.5L7 12.5" />
        <path d="M11.5 12.5H7V8" />
    </>,
);
export const ArrowOutIcon = ico(
    <>
        <path d="M7 13.5L13 7.5" />
        <path d="M8.5 7.5H13V12" />
    </>,
);
export const VoicemailIcon = ico(
    <>
        <circle cx="6" cy="11" r="3" />
        <circle cx="14" cy="11" r="3" />
        <path d="M6 14h8" />
    </>,
);
export const RefreshIcon = ico(
    <>
        <path d="M15.5 6.5A6 6 0 1 0 16 10" />
        <path d="M15.5 3v3.7H11.8" />
    </>,
);
export const PlayIcon = ico(<path d="M7 5.2l8 4.8-8 4.8z" />, 16);
/** Kontakt öffnen (Anruf hat einen Kontakt). */
export const PersonIcon = ico(
    <>
        <circle cx="10" cy="7" r="3.2" />
        <path d="M4 16.5c.8-3 3.2-4.6 6-4.6s5.2 1.6 6 4.6" />
    </>,
);
/** Kontakt zuordnen oder anlegen (Anruf ohne Kontakt). */
export const PersonAddIcon = ico(
    <>
        <circle cx="8.5" cy="7" r="3.2" />
        <path d="M2.8 16.5c.8-3 3-4.6 5.7-4.6 1.3 0 2.5.4 3.4 1" />
        <path d="M15.5 11v5.5M12.75 13.75h5.5" />
    </>,
);

export const DIRECTION_LABEL: Record<CallDirection, string> = {
    inbound: "Eingehend",
    outbound: "Ausgehend",
    internal: "Intern",
};

// Verpasst = inbound UND nicht erreicht (gespiegelt aus call-log.service.ts).
const MISSED_STATUS = new Set(["NO ANSWER", "BUSY", "FAILED", "CONGESTION"]);
export function isMissedInbound(c: CallLogDto): boolean {
    if (c.direction !== "inbound") return false;
    const talk = c.talkDurationSec ?? 0;
    if (talk > 0) return false;
    const status = String(c.status ?? "").toUpperCase();
    if (status.includes("ANSWER") && !status.includes("NO")) return false;
    if (MISSED_STATUS.has(status)) return true;
    // Inbound ohne Gesprächsdauer und ohne Erfolgs-Status → nicht erreicht.
    return true;
}

/**
 * „Offen verpasst" = verpasster Eingehender (`isMissedInbound`), der noch NICHT
 * erledigt ist (`resolvedAt == null`). EINZIGE Quelle für den Verpasst-Tab UND die
 * Verpasst-Badges (Telefon-Seite & Nav-Badge) — Liste und Zähler können so nicht
 * auseinanderlaufen.
 *
 * Abgearbeitete Anrufe verlassen damit den Verpasst-Tab und stehen nur noch im
 * Verlauf. `resolvedAt` deckt alle drei Wege ab: das Erledigt-Häkchen, ein aus dem
 * Anruf erstellter Vorgang und eine versendete Follow-up-Mail setzen es
 * (`callVorgangFlow` / `callRueckrufVorgang` patchen `resolved: true`).
 *
 * `vorgangId` ist hier BEWUSST kein Kriterium: das Backend hängt jeden neu
 * eingehenden Anruf automatisch an den einen aktiven Vorgang seines Kontakts
 * (`call-log.service.ts:autoAssignActiveVorgang`). Ein gesetztes `vorgangId` heißt
 * also nicht „bearbeitet" — sonst verschwände ein Rückruf, den nie jemand gesehen
 * hat, aus Liste und Badges, nur weil beim Kontakt ein Vorgang offen ist.
 */
export function isOpenMissed(c: CallLogDto): boolean {
    return isMissedInbound(c) && !c.resolvedAt;
}

// PBX-Status (ANSWERED/NO ANSWER/…) → deutsches Label. Unbekannte/leere Werte
// werden unverändert (bzw. „—") durchgereicht.
const STATUS_DE: Record<string, string> = {
    "ANSWERED": "Angenommen",
    "NO ANSWER": "Keine Antwort",
    "NOANSWER": "Keine Antwort",
    "BUSY": "Besetzt",
    "FAILED": "Fehlgeschlagen",
    "CONGESTION": "Überlastet",
    "VOICEMAIL": "Mailbox",
    "REJECTED": "Abgelehnt",
};
export function statusLabel(status?: string | null): string {
    const raw = String(status ?? "").trim();
    if (!raw) return "—";
    return STATUS_DE[raw.toUpperCase()] ?? raw;
}

export const fmtDuration = (secs?: number | null): string => {
    if (secs == null) return "—";
    const s = Math.max(0, Math.trunc(secs));
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

export const fmtTime = (iso: string): string => {
    try {
        return new Date(iso).toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

/**
 * Tag + Monat ohne Jahr (Anrufliste): „11.07.". Ein unlesbares Datum ergibt einen
 * leeren String — `toLocaleDateString` wirft dabei NICHT, sondern lieferte sonst
 * „Invalid Date" mitten in die Zeile.
 */
export const fmtDayMonth = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

export const fmtDate = (iso: string): string => {
    try {
        return new Date(iso).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

/** Datum + Uhrzeit kombiniert (für „Erledigt am …"). */
export const fmtDateTime = (iso: string): string => {
    const d = fmtDate(iso);
    const t = fmtTime(iso);
    return t ? `${d}, ${t}` : d;
};

/**
 * Normalisiert eine Wähl-Zeichenkette für die PBX: ein führendes „+" bleibt
 * erhalten, ansonsten werden nur Ziffern sowie die Feature-Code-Zeichen `*`/`#`
 * behalten. Buchstaben, Leerzeichen, Klammern, Slash und Bindestrich (z. B.
 * DB-/Anzeige-Formatierung wie „+33 (0)1 23 / 45" oder eine versehentliche
 * Texteingabe „abc123") fallen weg. Ergebnis ist eine Zeichenkette, die das SDK
 * direkt an die Telefonanlage geben kann.
 */
export const normalizeDialNumber = (raw: string): string => {
    const trimmed = (raw ?? "").trim();
    const plus = trimmed.startsWith("+") ? "+" : "";
    const rest = (plus ? trimmed.slice(1) : trimmed).replace(/[^0-9*#]/g, "");
    return plus + rest;
};

export const initialsOf = (name?: string | null): string | null => {
    if (!name) return null;
    const parts = name.split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

// Gegenstelle (Nummer der anderen Seite) — für Rückruf/Anzeige.
export const counterpartyNumber = (c: CallLogDto): string | null => {
    if (c.direction === "outbound") return c.toNumber ?? null;
    // T-51: Bei einem internen Anruf, den der Leser selbst geführt hat, steht
    // in `fromNumber` seine EIGENE Durchwahl — Gegenstelle ist dann `toNumber`.
    if (c.direction === "internal") {
        const ziffern = (n?: string | null) => (n ?? "").replace(/\D/g, "");
        const eigene = ziffern(c.extension);
        if (eigene && ziffern(c.fromNumber) === eigene && c.toNumber) return c.toNumber;
    }
    return c.fromNumber ?? c.toNumber ?? null;
};

/**
 * Wer oben in der Anruf-Zeile steht: der KMVS-Kontakt, sonst (T-51) der Name
 * des Kollegen hinter einer internen Durchwahl, sonst die Nummer.
 */
export const displayNameOf = (c: CallLogDto): string => {
    if (c.contactDisplayName) return c.contactDisplayName;
    const kollege = c.counterpartyName?.trim();
    if (kollege) return kollege;
    return counterpartyNumber(c) ?? "Unbekannt";
};

/** Initialen für den Avatar — vom Kontakt, sonst vom Kollegen (T-51). */
export const anzeigeInitialen = (c: CallLogDto): string | null =>
    initialsOf(c.contactDisplayName || c.counterpartyName?.trim() || null);

/**
 * T-12: Die Unterzeile einer Anruf-Zeile — Firma, Nummer, Status, Team,
 * Weiterleitung. Die Firma steht vorn: Sie beantwortet die erste Frage
 * („wer ist das?") zusammen mit dem Namen darüber. Die Nummer steht nur dann
 * hier, wenn oben ein Name statt der Nummer steht.
 */
export const unterzeileTeile = (c: CallLogDto): string[] => {
    const teile: string[] = [];
    const firma = (c.contactCompanyName ?? "").trim();
    if (firma && !c.contactIsCompany) teile.push(firma);
    const num = counterpartyNumber(c);
    if (num && displayNameOf(c) !== num) teile.push(num);
    if (isMissedInbound(c)) teile.push(c.resolvedAt ? "Erledigt" : "Verpasst");
    else if (c.status) teile.push(statusLabel(c.status));
    // Wem der Anruf gilt, wenn es nicht der Leser allein ist: Bei einem
    // verpassten Team-Anruf das Team, bei einer Weiterleitung der Kollege, der
    // abgenommen hat. Ohne das steht die Zeile ohne Begründung in der Liste.
    if (c.missedGroupName) teile.push(c.missedGroupName);
    const weiter = weitergeleitetAn(c);
    if (weiter) teile.push(`angenommen von ${weiter}`);
    return teile;
};

// ── Anzeigename im Anruf-Popup ──────────────────────────────────────────────

/**
 * Intern = Durchwahl statt Rufnummer. Die Grenze ist nicht frei gewählt, sondern
 * dieselbe wie im Backend: `ContactPhoneResolverService.resolve` steigt unter
 * 6 Ziffern aus, weil ein Suffix-Match darunter beliebige Kontakte träfe. Unter
 * dieser Grenze KANN KMVS also nichts wissen — dort bleibt die Anlage die Quelle.
 */
export const istInterneNummer = (number?: string | null): boolean => {
    const ziffern = (number ?? "").replace(/\D/g, "");
    return ziffern.length > 0 && ziffern.length < 6;
};

export interface AnrufAnzeigeQuelle {
    number: string | null;
    /** Name, den die Telefonanlage mitschickt (ihr eigenes Telefonbuch). */
    pbxName: string | null;
    /** Anzeigename des aufgelösten KMVS-Kontakts; null = kein Treffer. */
    kontaktName?: string | null;
}

/**
 * Wessen Name im Popup steht.
 *
 * Maßgeblich ist der KMVS-Kontakt, nicht das Telefonbuch der Telefonanlage: Die
 * dort gepflegten Namen sind veraltet und widersprechen dem, was im CRM steht —
 * genau darüber haben sich die Mitarbeiter beschwert. Der Anlagen-Name bleibt
 * nur dort die Quelle, wo KMVS prinzipbedingt nichts wissen kann: bei internen
 * Durchwahlen (s. {@link istInterneNummer}).
 *
 * Bei einer externen Nummer OHNE Kontakt zeigen wir bewusst die nackte Nummer.
 * Ein Anlagen-Name sähe aus wie ein bekannter Kontakt, obwohl KMVS ihn nicht
 * kennt — und würde verdecken, dass die Nummer nach dem Gespräch zugeordnet
 * werden sollte. Das gilt auch, solange die Auflösung noch läuft: lieber die
 * Nummer stehen lassen als ein Name, der eine Sekunde später wechselt.
 */
export const anrufAnzeigeName = ({ number, pbxName, kontaktName }: AnrufAnzeigeQuelle): string => {
    const kontakt = kontaktName?.trim();
    if (kontakt) return kontakt;
    if (istInterneNummer(number)) return pbxName?.trim() || number || "Unbekannter Anrufer";
    return number || pbxName?.trim() || "Unbekannter Anrufer";
};

/**
 * Ob nach einem beendeten Anruf gefragt wird, zu wem die Nummer gehört.
 *
 * Die Bedingungen sind bewusst eng — ein Fenster, das zu oft aufgeht, wird
 * weggeklickt und nutzt dann nie wieder etwas:
 *  - Es muss GESPROCHEN worden sein. Ein verpasster oder weggedrückter Anruf
 *    sagt nichts darüber aus, wer angerufen hat; bei jedem Werbeanruf zu
 *    fragen wäre eine Zumutung.
 *  - Interne Durchwahlen bleiben außen vor: Kollegen sind keine Kontakte, und
 *    KMVS kann unter 6 Ziffern ohnehin nichts auflösen (s. istInterneNummer).
 *  - Nummern, zu denen schon „Später" gesagt wurde, kommen nicht wieder.
 * Ob es zu der Nummer einen Kontakt gibt, prüft der Aufrufer zusätzlich beim
 * Server — das steht hier absichtlich nicht drin, weil es nicht zu raten ist.
 */
export const sollNachAnrufFragen = (anruf: {
    number: string | null;
    gesprochen: boolean;
    bereitsGefragt?: ReadonlySet<string>;
}): boolean => {
    if (!anruf.gesprochen) return false;
    const nummer = anruf.number?.trim();
    if (!nummer) return false;
    if (istInterneNummer(nummer)) return false;
    return !anruf.bereitsGefragt?.has(nummer);
};

/**
 * Die Nummer als Unterzeile — nur, wenn darüber ein NAME steht (sonst stünde die
 * Nummer zweimal untereinander).
 */
export const anrufAnzeigeNummer = (quelle: AnrufAnzeigeQuelle): string | null => {
    const { number } = quelle;
    if (!number) return null;
    return anrufAnzeigeName(quelle) === number ? null : number;
};

// ── Der Weg eines Anrufs ────────────────────────────────────────────────────

/** Ein Schritt des Wegs, fertig zum Anzeigen. */
export interface CallPathStep {
    /** Wen oder was der Anruf erreicht hat („Larissa Konca", „Team Service"). */
    label: string;
    /** Was dabei herauskam („angenommen", „nicht angenommen", „Mailbox"). */
    outcome: string;
    /** Hat hier jemand abgehoben? Steuert die Hervorhebung. */
    answered: boolean;
    kind: string;
}

const ANGENOMMEN = new Set(["ANSWERED", "ANSWER"]);

const outcomeLabel = (disposition?: string | null, kind?: string): string => {
    const d = String(disposition ?? "").toUpperCase();
    if (ANGENOMMEN.has(d)) return "angenommen";
    if (d === "VOICEMAIL") return kind === "voicemail" ? "Mailbox" : "auf die Mailbox";
    if (d === "NO ANSWER") return "nicht angenommen";
    if (d === "BUSY") return "besetzt";
    if (d === "ABANDONED") return "aufgelegt";
    if (d === "FAILED") return "fehlgeschlagen";
    return d ? d.toLowerCase() : "—";
};

/**
 * Der Weg des Anrufs als lesbare Schritte: „88 klingelt vergeblich → Team
 * Service → Larissa Konca nimmt an → vermittelt an Lindau-Langhans."
 *
 * Voicemail-Abschnitte fallen raus, wenn davor schon eine Gruppe steht — dass
 * ein unbeantworteter Team-Anruf auf der Mailbox landet, ist der Normalfall und
 * sagt dem Leser nichts Neues.
 */
export const callPath = (c: CallLogDto): CallPathStep[] => {
    const legs = c.legs ?? [];
    if (legs.length === 0) return [];
    const hatGruppe = legs.some((l) => l.targetKind === "queue" || l.targetKind === "ringgroup");
    return legs
        .filter((l) => !(l.targetKind === "voicemail" && hatGruppe))
        .map((l) => ({
            label: l.userName ?? l.targetName ?? l.targetNumber ?? "Unbekannt",
            outcome: outcomeLabel(l.disposition, l.targetKind),
            answered: ANGENOMMEN.has(String(l.disposition ?? "").toUpperCase()),
            kind: l.targetKind,
        }));
};

/**
 * Hat dieser Anruf einen Weg, der über das Offensichtliche hinausgeht? Ein
 * Anruf mit genau einem Abschnitt ging direkt durch — da gibt es nichts zu
 * erzählen.
 */
export const hatWeg = (c: CallLogDto): boolean => callPath(c).length > 1;

/**
 * Kurzfassung für die Liste: Wer hat den Anruf am Ende angenommen, wenn es
 * nicht der war, bei dem es zuerst klingelte? Sonst null.
 */
export const weitergeleitetAn = (c: CallLogDto): string | null => {
    const schritte = callPath(c);
    if (schritte.length < 2) return null;
    const ersterAngenommen = schritte[0].answered;
    if (ersterAngenommen) return null;
    const letzterAngenommen = [...schritte].reverse().find((s) => s.answered && s.kind === "extension");
    return letzterAngenommen?.label ?? null;
};
